import React, { useState } from 'react';
import { MapPin, Map as MapIcon, Layers, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import logo from '../../logo.png';

export const WelcomeModal = ({ onSelect, isOpen }) => {
  const { theme, toggleTheme } = useTheme();
  const [selectedType, setSelectedType] = useState('satellite');

  if (!isOpen) return null;

  const mapOptions = [
    {
      id: 'satellite',
      label: 'Satélite',
      description: 'Vista aérea real da região',
      image: '/satelite.png',
      icon: <Layers className="w-4 h-4" />,
      color: 'cyan',
      activeText: 'text-cyan-400',
      activeBorder: 'border-cyan-500',
      activeBg: theme === 'dark' ? 'bg-cyan-500/10' : 'bg-cyan-50',
    },
    {
      id: 'roadmap',
      label: 'Padrão',
      description: 'Mapa vetorial com ruas',
      image: '/padrao.png',
      icon: <MapIcon className="w-4 h-4" />,
      color: 'blue',
      activeText: 'text-blue-400',
      activeBorder: 'border-blue-500',
      activeBg: theme === 'dark' ? 'bg-blue-500/10' : 'bg-blue-50',
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className={`relative border rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-300 ${theme === 'dark'
          ? 'bg-slate-800 border-cyan-500/30 shadow-cyan-500/10'
          : 'bg-white border-slate-200 shadow-slate-200/50'
        }`}>

        {/* Theme toggle no canto */}
        <button
          onClick={toggleTheme}
          title={`Mudar para modo ${theme === 'dark' ? 'Claro' : 'Escuro'}`}
          className={`absolute top-4 right-4 p-2 rounded-full transition-all duration-300 ${theme === 'dark'
              ? 'bg-slate-700 text-yellow-400 hover:bg-slate-600'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        <div className="text-center mb-6">
          <div className="relative inline-block mb-4">
            <div className="absolute inset-0 bg-cyan-500/20 blur-xl rounded-full" />
            <img src={logo} alt="Logo" className="relative h-16 mx-auto drop-shadow-[0_0_12px_rgba(6,182,212,0.6)]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-3">
            Bem-vindo ao Mapa Cultural
          </h2>
          <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
            Explore a riqueza cultural da Região Imediata de Piripiri.
            Descubra eventos, festivais, artesanato e muito mais.
          </p>
        </div>

        <div className="mb-6">
          <p className={`text-xs font-semibold uppercase tracking-wider text-center mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
            }`}>
            Escolha o estilo do mapa:
          </p>
          <div className="grid grid-cols-2 gap-3">
            {mapOptions.map((opt) => {
              const isSelected = selectedType === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSelectedType(opt.id)}
                  className={`relative rounded-xl border-2 transition-all duration-200 overflow-hidden group ${isSelected
                      ? `${opt.activeBorder} ${opt.activeBg} shadow-lg`
                      : theme === 'dark'
                        ? 'border-slate-700 bg-slate-900 hover:border-slate-500'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:shadow-sm'
                    }`}
                >
                  {isSelected && (
                    <div className={`absolute top-2 right-2 z-10 w-5 h-5 rounded-full flex items-center justify-center ${opt.color === 'cyan' ? 'bg-cyan-500' : 'bg-blue-500'
                      }`}>
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  <div className="w-full h-28 overflow-hidden">
                    <img
                      src={opt.image}
                      alt={`Preview ${opt.label}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {!isSelected && (
                      <div className="absolute inset-0 bg-black/20 transition-opacity duration-200 group-hover:bg-black/10" />
                    )}
                  </div>

                  <div className="p-3 text-left">
                    <div className={`flex items-center gap-1.5 font-semibold text-sm mb-0.5 ${isSelected ? opt.activeText : (theme === 'dark' ? 'text-slate-300' : 'text-slate-700')
                      }`}>
                      {opt.icon}
                      {opt.label}
                    </div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
                      }`}>
                      {opt.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => onSelect(selectedType)}
          className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-cyan-500/30 transform active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <MapPin className="w-4 h-4" />
          Começar Exploração
        </button>
      </div>
    </div>
  );
};
